import { isDefined } from "@clipboard-health/util-ts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useLatestDefinedValue } from "../../utils/useLatestDefinedValue";
import { isOfferFreshForShift } from "./isOfferValidForShift";
import { getOfferCacheKey, useCachedOffer, useCreateOffer } from "./useCreateOffer";

interface AutoRefreshOfferOptions {
  enabled?: boolean;
}

/**
 * Automatically refreshes an offer for a shift if it has expired.
 *
 * This is useful for shifts that are displayed in a list, where the offer may expire
 * after the shift is displayed.
 */
export function useAutoRefreshOffer(
  params: { shiftId?: string },
  options: AutoRefreshOfferOptions = {}
) {
  const { shiftId } = params;
  const { enabled = true } = options;

  const queryClient = useQueryClient();

  const {
    mutate: createOffer,
    isError,
    isLoading: isMutationLoading,
    isIdle,
  } = useCreateOffer(params);
  const cachedOffer = useCachedOffer(shiftId);

  const offer = useLatestDefinedValue(cachedOffer);

  const shouldCreateOffer =
    isDefined(shiftId) && enabled && !isOfferFreshForShift({ shiftId, offer });

  // `useAutoRefreshOffer` has read semantics but relies on `useCreateOffer`
  // which has write semantics. Specifically, `useCreateOffer` only has
  // `isLoading === true` once the mutation function has been triggered.
  // However, what we want from a read is to also consider the read to be
  // loading prior to when the mutation is triggered iff the read is enabled.
  //
  // Tanstack semantics use isLoading for the initial load and isFetching for
  // all loads include refetches while the data is available client side.
  const isFetching = shouldCreateOffer ? isIdle || isMutationLoading : isMutationLoading;
  const isLoading = !isDefined(offer) && isFetching;

  useEffect(() => {
    if (isError) {
      // Can't create an offer, so don't try again
      return;
    }

    if (isDefined(shiftId) && shouldCreateOffer) {
      const cacheKey = getOfferCacheKey(shiftId);
      const hasPendingMutation = queryClient.getMutationCache().find({
        exact: false,
        fetching: true,
        mutationKey: cacheKey,
      });

      if (!hasPendingMutation) {
        createOffer({ shiftId });
      }
    }
  }, [shiftId, shouldCreateOffer, queryClient, createOffer, isError]);

  return {
    data: { offer },
    isLoading,
    isFetching,
    isError,
  };
}
